import { rpcSlice } from '../../../store/api/rpcSlice';

export interface OnboardingMember {
  form_response_id: string;
  person_id: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  company: string | null;
  submitted_at: string;
}

export interface MemberOnboarding extends OnboardingMember {
  answers: unknown;
}

export interface BacktestCase {
  form_response_id?: string;
  member_person_id: string;
  member_name?: string;
  member_email?: string;
  actual_coach_person_id: string;
  actual_coach_name?: string;
  actual_coach_email?: string;
  matched_at: string;
  actual_remaining_capacity?: number;
  actual_matching_status?: string;
  actual_coach_status?: string;
  actual_is_active: boolean;
  actual_eligible_now: boolean;
  predicted_coach_person_ids?: string[];
  predicted_top_coach_person_id?: string;
  prediction_matched_actual_top1?: boolean;
  prediction_matched_actual_top5?: boolean;
  prediction_error?: string;
  onboarding_submitted_at?: string;
}

export interface BacktestResponse {
  prediction: {
    prediction_case_count: number;
    top1_hit_count: number;
    top5_hit_count: number;
    top1_hit_rate: number;
    top5_hit_rate: number;
    prediction_errors: number;
    runnable_cases: number;
    resolved_via_linked_person: number;
    resolved_via_email_fallback: number;
    unresolved_cases: number;
  };
  analysis: {
    case_count: number;
    eligible_now_count: number;
    ineligible_now_count: number;
    eligible_now_rate: number;
    ineligible_breakdown: {
      inactive_coach: number;
      do_not_match_status: number;
      no_capacity: number;
    };
    recommendations: BacktestRecommendation[];
  };
  recommendations: BacktestRecommendation[];
  prompt: {
    slug: string;
    version: number;
    updated_at: string;
    text: string;
  };
  case_count: number;
  eligible_now_rate: number;
  cases: BacktestCase[];
}

export interface BacktestJobResponse {
  job_id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  case_limit: number;
  case_count: number;
  prediction_case_count: number;
  error_message?: string;
  result?: BacktestResponse;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  updated_at: string;
}

export interface DataStatusSyncInfo {
  synced_count: number;
  missing_count: number;
  last_synced_at: string | null;
}

export interface DataStatusResponse {
  total_coaches: number;
  eligible_coaches: number;
  excluded_coaches: {
    dnm: number;
    offboarding: number;
    non_active: number;
    no_capacity: number;
    incomplete_profile: number;
  };
  brag_sheet: DataStatusSyncInfo;
  capacity_sheet: DataStatusSyncInfo;
}

export interface BacktestRecommendation {
  id: string;
  priority: 'high' | 'medium' | 'low';
  title: string;
  why: string;
  prompt_guidance: string;
}

export const coachMatchingApi = rpcSlice.injectEndpoints({
  endpoints: (builder) => ({
    getDataStatus: builder.query<DataStatusResponse, void>({
      query: () => ({
        url: '/admin/coach-matching/data-status',
        method: 'GET',
      }),
      providesTags: ['DataStatus'],
    }),
    listOnboardingMembers: builder.query<OnboardingMember[], void>({
      query: () => ({
        url: '/admin/coach-matching/members',
        method: 'GET',
      }),
    }),
    getMemberOnboarding: builder.query<MemberOnboarding, string>({
      query: (formResponseId) => ({
        url: `/admin/coach-matching/members/${formResponseId}`,
        method: 'GET',
      }),
    }),
    triggerBragSheetSync: builder.mutation<{ status: string; brag_sheets_upserted: number }, void>({
      query: () => ({
        url: '/admin/coach-matching/sync/brag-sheet',
        method: 'POST',
      }),
      invalidatesTags: ['DataStatus'],
    }),
    triggerCapacitySheetSync: builder.mutation<
      { status: string; capacity_rows_upserted: number },
      void
    >({
      query: () => ({
        url: '/admin/coach-matching/sync/capacity-sheet',
        method: 'POST',
      }),
      invalidatesTags: ['DataStatus'],
    }),
    enqueueBacktest: builder.mutation<BacktestJobResponse, { limit?: number } | undefined>({
      query: (body) => ({
        url: '/admin/coach-matching/backtest/jobs',
        method: 'POST',
        body,
      }),
    }),
    getBacktestJob: builder.query<BacktestJobResponse, string>({
      query: (jobId) => ({
        url: `/admin/coach-matching/backtest/jobs/${jobId}`,
        method: 'GET',
      }),
    }),
    runBacktest: builder.mutation<BacktestResponse, { limit?: number } | undefined>({
      query: (body) => ({
        url: '/admin/coach-matching/backtest',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetDataStatusQuery,
  useListOnboardingMembersQuery,
  useGetMemberOnboardingQuery,
  useLazyGetMemberOnboardingQuery,
  useTriggerBragSheetSyncMutation,
  useTriggerCapacitySheetSyncMutation,
  useEnqueueBacktestMutation,
  useLazyGetBacktestJobQuery,
  useRunBacktestMutation,
} = coachMatchingApi;
